import { useEffect, useState } from "react";
import { tasks, products, profiles } from "../lib/api";
import type { Task, Product, Profile } from "../lib/api";
import { Plus, Play, Square, Trash2, RefreshCw, Package, Clock, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import clsx from "clsx";

type TaskFormData = {
  productId: string;
  profileId: string;
  quantity: number;
  maxPrice: string;
};

const emptyFormData: TaskFormData = {
  productId: "",
  profileId: "",
  quantity: 2, // Default to 2 (common limit for limited drops)
  maxPrice: "",
};

export default function Tasks() {
  const [taskList, setTaskList] = useState<Task[]>([]);
  const [productList, setProductList] = useState<Product[]>([]);
  const [profileList, setProfileList] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [formData, setFormData] = useState<TaskFormData>(emptyFormData);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fetchData = async () => {
    try {
      const [t, p, pr] = await Promise.all([tasks.list(), products.list(), profiles.list()]);
      setTaskList(t);
      setProductList(p);
      setProfileList(pr);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditingTaskId(null);
    setFormData(emptyFormData);
    setShowAdvanced(false);
    setShowModal(true);
  };

  const openEdit = (task: Task) => {
    setEditingTaskId(task.id);
    setFormData({
      productId: task.product.id,
      profileId: task.profile.id,
      quantity: task.quantity,
      maxPrice: task.maxPrice?.toString() || "",
    });
    setShowAdvanced(!!task.maxPrice);
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        productId: formData.productId,
        profileId: formData.profileId,
        quantity: formData.quantity,
        maxPrice: formData.maxPrice ? parseFloat(formData.maxPrice) : undefined,
      };

      if (editingTaskId) {
        await tasks.update(editingTaskId, data);
      } else {
        await tasks.create(data);
      }
      setShowModal(false);
      setFormData(emptyFormData);
      setEditingTaskId(null);
      setShowAdvanced(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save task", error);
      alert("Failed: " + (error as Error).message);
    }
  };

  const handleStart = async (id: string) => {
    try {
      await tasks.start(id);
      fetchData();
    } catch (error) {
      console.error("Failed to start task", error);
    }
  };

  const handleStop = async (id: string) => {
    try {
      await tasks.stop(id);
      fetchData();
    } catch (error) {
      console.error("Failed to stop task", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this task?")) return;
    try {
      await tasks.delete(id);
      fetchData();
    } catch (error) {
      console.error("Failed to delete task", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">Tasks ({taskList.length})</h2>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          New Task
        </button>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-4">
              {editingTaskId ? "Edit Task" : "Create Task"}
            </h3>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="label">Product</label>
                <select
                  className="input"
                  value={formData.productId}
                  onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                  required
                  disabled={!!editingTaskId}
                >
                  <option value="">Select product...</option>
                  {productList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.retailer})
                    </option>
                  ))}
                </select>
                {editingTaskId && <p className="text-xs text-gray-500 mt-1">Product cannot be changed after creation</p>}
              </div>
              <div>
                <label className="label">Profile</label>
                <select
                  className="input"
                  value={formData.profileId}
                  onChange={(e) => setFormData({ ...formData, profileId: e.target.value })}
                  required
                >
                  <option value="">Select profile...</option>
                  {profileList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Quantity</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  max="4"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: Math.min(4, Math.max(1, parseInt(e.target.value) || 1)) })}
                />
                <p className="text-xs text-gray-500 mt-1">Task completes after purchasing this quantity</p>
              </div>

              {/* Advanced Options Toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
              >
                {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="space-y-4 pt-2 border-t border-dark-600">
                  <div>
                    <label className="label">Max Price ($)</label>
                    <input
                      type="number"
                      className="input"
                      placeholder="No limit"
                      value={formData.maxPrice}
                      onChange={(e) => setFormData({ ...formData, maxPrice: e.target.value })}
                    />
                    <p className="text-xs text-gray-500 mt-1">Skip checkout if price exceeds this</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTaskId ? "Save" : "Create Task"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task List */}
      {taskList.length === 0 ? (
        <div className="card text-center py-12">
          <Package size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No tasks yet. Create one to start monitoring!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {taskList.map((task) => (
            <div key={task.id} className="card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {task.product.imageUrl ? (
                    <img
                      src={task.product.imageUrl}
                      alt={task.product.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-dark-600 rounded-lg flex items-center justify-center">
                      <Package size={24} className="text-gray-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold">{task.product.name}</h4>
                    <p className="text-sm text-gray-400">
                      {task.product.retailer} | Target: {task.quantity} | Profile: {task.profile.name}
                    </p>
                    {task.maxPrice && (
                      <p className="text-xs text-gray-500 mt-1">Max price: ${task.maxPrice}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Queue Status */}
                  {task.status === "in_queue" && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-warning/20 rounded-lg">
                      <Clock size={14} className="text-warning" />
                      <span className="text-sm text-warning">
                        Queue #{task.queuePosition || "?"} 
                        {task.queueEstimatedWait && ` (~${task.queueEstimatedWait}m)`}
                      </span>
                    </div>
                  )}
                  {/* Stats */}
                  <div className="text-right">
                    <p className="text-sm">
                      <span className={task.successCount >= task.quantity ? "text-success" : "text-primary-400"}>
                        {task.successCount}
                      </span>
                      <span className="text-gray-500"> / {task.quantity}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {task.totalSpent > 0 ? `$${task.totalSpent.toFixed(2)} spent` : "No purchases yet"}
                    </p>
                  </div>
                  {/* Status */}
                  <span
                    className={clsx(
                      "status-badge",
                      task.status === "monitoring" && "bg-blue-500/20 text-blue-400",
                      task.status === "in_queue" && "bg-warning/20 text-warning",
                      task.status === "success" && "bg-success/20 text-success",
                      task.status === "failed" && "bg-error/20 text-error",
                      task.status === "idle" && "bg-gray-500/20 text-gray-400"
                    )}
                  >
                    {task.status}
                  </span>
                  {/* Actions */}
                  <div className="flex gap-1">
                    {task.status === "monitoring" || task.status === "in_queue" ? (
                      <button
                        onClick={() => handleStop(task.id)}
                        className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Stop"
                      >
                        <Square size={18} className="text-gray-400" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStart(task.id)}
                        className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                        title="Start"
                      >
                        <Play size={18} className="text-success" />
                      </button>
                    )}
                    <button
                      onClick={() => openEdit(task)}
                      className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                      title="Edit"
                      disabled={task.status === "monitoring" || task.status === "in_queue"}
                    >
                      <Pencil size={18} className={clsx(
                        task.status === "monitoring" || task.status === "in_queue"
                          ? "text-gray-600"
                          : "text-gray-400 hover:text-primary-500"
                      )} />
                    </button>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="p-2 hover:bg-dark-600 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} className="text-error" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
